import { Component, OnDestroy, ElementRef, signal, input, viewChild, computed, effect, afterNextRender, untracked } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Blast, Charges, Hole, HoleGeometryDto, HoleService } from 'src/app/services/hole.service';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { UtilisateurService } from '../../services/utilisateur.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ExportFileModalComponent, FileExportType } from './export-file-modal/export-file-modal.component';
import { SaveTerrainModalComponent, SaveTerrainChoice } from './save-terrain-modal/save-terrain-modal.component';
import type { ExportResult } from './export-file-modal/export-file-modal.component';
import { ModelLoaderService, ModelLoadResult } from '../../services/model-loader.service';
import { TerrainLoaderService, TerrainLoadResult } from '../../services/terrain-loader.service';
import { HttpService, BlastLineViewDto, BlastLineTypeProto } from '../../services/http.service';
import { FonctionsService } from '../../services/fonctions-partagees.service';
import { Bulk } from '../../bo/products/bulk';
import { Cartridge } from '../../bo/products/cartridge';
import { ProductType } from '../../bo/products/productType';
import { forkJoin } from 'rxjs';
import type { Position } from 'geojson';
import {
  GEOLOGY_FALLBACK_COLOR, GeologyBlock, GeologyKind, GeologyModel, HoleTrack, Vec2,
  buildBlocks, edgeKey, exteriorFaces, normalizeHexColor,
} from './geology-model';
import * as protobuf from 'protobufjs';
import { ButtonComponent } from '@libs/front/components/button/button.component';

// Extend Charges type to include holeName for info panel
export interface ChargesWithHoleName extends Charges {
  holeName?: string;
  productName?: string;
  isBulk?: boolean;
}

@Component({
  selector: 'app-blast3-dviewer',
  templateUrl: './blast3-dviewer.component.html',
  styleUrls: ['./blast3-dviewer.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslateModule, DecimalPipe, ButtonComponent]
})
export class Blast3DViewerComponent implements OnDestroy {
  // Signal-based inputs and view queries
  readonly tirId = input<number>();
  /** Modèle géologique à construire sous les trous, posé par la légende de la vue Géologie ; null le retire. */
  readonly geologyModel = input<GeologyModel | null>(null);
  readonly canvasRef = viewChild<ElementRef<HTMLCanvasElement>>('canvas3d');
  readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  private resizeObserver?: ResizeObserver;

  private canvas: HTMLCanvasElement;
  private readonly scene = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera();
  private renderer: THREE.WebGLRenderer;
  private offsetPoint = new THREE.Vector3(0, 0, 0);
  /**
   * Vrai dès qu'un élément de la scène (trous, terrain chargé, OBJ importé) a fixé offsetPoint.
   * Sa valeur ne suffit pas à le dire : un premier trou en (0,0,0) fixe un vrai repère à zéro.
   */
  private frameIsFixed = false;
  private controls: OrbitControls;

  files: File[] = [];

  selectedObjFile: File | null = null;
  selectedMtlFile: File | null = null;
  selectedTextureFiles: File[] = [];
  private readonly loadedObjModels = signal<THREE.Object3D[]>([]);
  readonly hasLoadedObjModels = computed(() => this.loadedObjModels().length > 0);
  readonly isSavingTerrain = signal(false);

  chargeMeshes: { mesh: THREE.Mesh, charge: ChargesWithHoleName, originalColor: string }[] = [];
  readonly selectedChargeInfo = signal<ChargesWithHoleName | null>(null);

  /**
   * Têtes de trou cliquables. Les charges n'existent que sur un trou chargé : sans ces têtes,
   * un trou simplement foré n'offrait aucune prise au clic.
   */
  holeHeadMeshes: { mesh: THREE.Mesh, hole: any }[] = [];
  readonly selectedHoleInfo = signal<any | null>(null);

  /**
   * Charge surlignée. On retient la charge et non un cylindre : sur un trou dévié, une charge
   * est dessinée en plusieurs tronçons qui partagent la même fiche, et tous doivent s'allumer.
   */
  selectedCharge: ChargesWithHoleName | null = null;

  lastBlast: Blast | null = null;

  // Terrain data
  loadedTerrain: THREE.Group | null = null;
  readonly isLoadingTerrain = signal(false);
  readonly terrainLoadError = signal<string | null>(null);

  // ── Modèle géologique ─────────────────────────────────────────────────────
  /** Trajectoire de chaque trou dans la scène et sa longueur déclarée (publicGuid → trou), relevées en posant les trous. */
  private readonly holePaths = new Map<string, { path: THREE.Vector3[]; length: number }>();
  /** Nature du modèle affiché (titre du curseur), null sans modèle. */
  readonly geologyKind = signal<GeologyKind | null>(null);
  /** Vrai une fois les trous du tir posés : le modèle a besoin de leurs trajectoires. */
  private readonly holesReady = signal(false);
  /** Numéro du dernier chargement des trous : la réponse d'un chargement dépassé est ignorée. */
  private setupSeq = 0;
  /** Part du modèle affichée, du fond vers la surface (%), réglée au curseur par pas de 10 %. */
  readonly geologyPercent = signal(100);
  /** Tranches et cellules à partir desquelles `geologyBlocks` a été construit : le modèle n'est refait que si elles changent. */
  private geologyHoles: GeologyModel['holes'] | null = null;
  private geologyCells: GeologyModel['cells'] | null = null;
  /** Blocs du modèle, du fond vers la surface ; les catégories masquées restent dedans. */
  private geologyBlocks: GeologyBlock[] = [];
  /** Triangles du dessus de chaque cellule (indices dans son anneau), calculés une fois par cellule. */
  private readonly geologyTriangles = new Map<readonly Vec2[], number[][]>();
  /** Cellules qui partagent chaque arête (clé : ses deux extrémités au millimètre), pour ne pas dessiner les murs entre voisins. */
  private readonly geologyEdgeOwners = new Map<string, Set<GeologyBlock['rings']>>();
  /** Base du bloc le plus bas et épaisseur totale du modèle (m) : le curseur reste stable quand on masque une catégorie. */
  private geologyBottom = 0;
  private geologyThickness = 0;
  /** Couleurs et catégories masquées du dernier modèle posé, pour redessiner à chaque cran du curseur. */
  private geologyColors: GeologyModel['colors'] = new Map();
  private geologyHidden: GeologyModel['hidden'] = new Set();
  private geologyGroup: THREE.Group | null = null;

  /**
   * Un tir peut porter plusieurs terrains — typiquement avant et après forage —, le proto
   * étant un `repeated Terrain`. Chacun devient un enfant du groupe chargé : cette liste
   * les expose pour les afficher ou les masquer un par un. L'index correspond à celui de
   * loadedTerrain.children.
   */
  /** Terrains affichés, dans l'ordre de la scène ; protoIndex est leur rang dans le payload stocké. */
  readonly terrainLayers = signal<{ name: string; visible: boolean; protoIndex: number }[]>([]);

  // Blast lines data (crest and foot lines)
  private blastLinesGroup: THREE.Group | null = null;
  private lineProtoType: protobuf.Type | null = null;

  // Product lookup map (product.id -> { name, productType })
  private readonly productMap: Map<number, { name: string, productType: ProductType }> = new Map();

  private viewInitialized = false;

  constructor(
    private readonly holeService: HoleService,
    private readonly utilisateurService: UtilisateurService,
    private readonly modelLoaderService: ModelLoaderService,
    private readonly terrainLoaderService: TerrainLoaderService,
    private readonly httpService: HttpService,
    private readonly modalService: BsModalService,
    private readonly fonctionsService: FonctionsService
  ) {
    // Vue Géologie de l'onglet Trous : le modèle s'affiche dès que les trous du tir sont posés,
    // suit la légende (lithologies ou drill logs, catégories masquées) et disparaît avec la vue.
    effect(() => {
      const model = this.holesReady() ? this.geologyModel() : null;
      untracked(() => this.renderGeology(model));
    });

    // React to tirId changes
    effect(() => {
      const id = this.tirId();
      if (id !== undefined && this.viewInitialized) {
        this.updateScene();
      }
    });

    // Initialize after DOM render
    afterNextRender(() => {
      this.initializeView();
    });

    // Load product mappings
    forkJoin([
      this.httpService.get<Bulk[]>('Products/GetAllBulks'),
      this.httpService.get<Cartridge[]>('Products/GetAllCartridges'),
    ]).subscribe(([bulks, cartridges]) => {
      bulks.forEach(b => this.productMap.set(b.product.id, { name: b.explosive.fullName, productType: ProductType.Bulk }));
      cartridges.forEach(c => this.productMap.set(c.product.id, { name: c.explosive.fullName, productType: ProductType.Cartridge }));
    });
  }

  private isFirefox(): boolean {
    return navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
  }

  private calculateOptimalCanvasHeight(): number {
    const viewportHeight = window.innerHeight;
    const headerHeight = 100; // Ajuster selon votre layout
    const footerHeight = 50;  // Ajuster selon votre layout  
    const buttonHeight = 60;  // Hauteur du bouton "ADD_HOLES"
    const margin = 40;        // Marges supplémentaires
    
    const availableHeight = viewportHeight - headerHeight - footerHeight - buttonHeight - margin;
    const maxHeight = Math.min(availableHeight, viewportHeight * 0.8); // Maximum 80% de l'écran
    const minHeight = 400; // Hauteur minimale
    
    return Math.max(minHeight, maxHeight);
  }

  private adjustCanvasContainerHeight(): void {
    const canvasContainer = document.querySelector('.canvas-container') as HTMLElement;
    if (canvasContainer) {
      const optimalHeight = this.calculateOptimalCanvasHeight();
      canvasContainer.style.height = `${optimalHeight}px`;
      console.log(`Canvas container height adjusted to: ${optimalHeight}px`);
      this.camera = new THREE.PerspectiveCamera(75, canvasContainer.clientWidth /canvasContainer.clientHeight, 0.01, 5000);
      this.renderer.setSize(canvasContainer.clientWidth, canvasContainer.clientHeight);
    }
  }

  private debugCanvasSize(): void {
    if (this.canvas) {
      const container = this.canvas.parentElement;
      const containerStyle = container ? window.getComputedStyle(container) : null;
      const canvasStyle = window.getComputedStyle(this.canvas);
      
      console.log('Canvas Debug Info:', {
        browser: this.isFirefox() ? 'Firefox' : 'Other',
        viewportHeight: window.innerHeight,
        optimalHeight: this.calculateOptimalCanvasHeight(),
        canvasActualSize: { width: this.canvas.width, height: this.canvas.height },
        canvasClientSize: { width: this.canvas.clientWidth, height: this.canvas.clientHeight },
        canvasStyleSize: { width: canvasStyle.width, height: canvasStyle.height },
        containerSize: container ? { width: container.clientWidth, height: container.clientHeight } : null,
        containerStyle: containerStyle ? { width: containerStyle.width, height: containerStyle.height } : null,
        rendererSize: this.renderer.getSize(new THREE.Vector2())
      });
    }
  }

  private initializeView(): void {
    const canvasEl = this.canvasRef();
    if (!canvasEl) return;

    this.canvas = canvasEl.nativeElement;
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      logarithmicDepthBuffer: true
    });
    
    const delay = this.isFirefox() ? 100 : 0;
    setTimeout(() => {
      this.initializeCanvas();
      this.renderer.setClearColor(0x887755, 1);
      this.renderer.render(this.scene, this.camera);
      
      this.debugCanvasSize();
      
      this.setupCamera();
      this.setupScene();
      this.setupControls();
      this.viewInitialized = true;
      this.setupData();
      
      this.renderer.domElement.addEventListener('pointerdown', this.onPointerDown.bind(this));
      this.renderer.domElement.addEventListener('mousemove', this.onPointerMove.bind(this));
    }, delay);

    // The canvas fills its container: follow the container size (window resize, panel drag, 2D/3D toggle...)
    this.resizeObserver = new ResizeObserver(() => this.resizeCanvas());
    if (this.canvas.parentElement) this.resizeObserver.observe(this.canvas.parentElement);

    const clock = new THREE.Clock();
    this.renderer.setAnimationLoop(() => {
      const elapsedTime = clock.getElapsedTime();
      this.renderer.render(this.scene, this.camera);
    });
  }
  private updateScene(): void {
    while (this.scene.children.length > 0) {
      const child = this.scene.children.pop();
      if (child) {
        this.scene.remove(child);
      }
    }
    this.chargeMeshes = [];
    this.holeHeadMeshes = [];
    this.selectedChargeInfo.set(null);
    this.selectedHoleInfo.set(null);
    this.selectedCharge = null;
    this.loadedTerrain = null;
    this.terrainLayers.set([]);
    this.terrainLoadError.set(null);
    this.blastLinesGroup = null;
    // Scène vidée : plus rien ne fixe le repère tant que les trous du tir ne l'ont pas posé.
    this.frameIsFixed = false;

    this.setupScene();

    this.setupData();
  }
  
  private initializeCanvas(): void {
    const canvasContainer = this.canvas.parentElement;
    if (canvasContainer) {
      const computedStyle = window.getComputedStyle(canvasContainer);
      const width = canvasContainer.clientWidth - Number.parseFloat(computedStyle.paddingLeft) - Number.parseFloat(computedStyle.paddingRight);
      const height = canvasContainer.clientHeight - Number.parseFloat(computedStyle.paddingTop) - Number.parseFloat(computedStyle.paddingBottom);
      
      this.camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
      this.renderer.setSize(width, height, false);
      
      this.canvas.style.width = '100%';
      this.canvas.style.height = '100%';
      this.canvas.style.display = 'block';
      
      this.renderer.setPixelRatio(window.devicePixelRatio);
    }
  }

  private resizeCanvas(): void {
    const canvasContainer = this.canvas.parentElement;
    if (canvasContainer) {
      const computedStyle = window.getComputedStyle(canvasContainer);
      const width = canvasContainer.clientWidth - Number.parseFloat(computedStyle.paddingLeft) - Number.parseFloat(computedStyle.paddingRight);
      const height = canvasContainer.clientHeight - Number.parseFloat(computedStyle.paddingTop) - Number.parseFloat(computedStyle.paddingBottom);
      if (width <= 0 || height <= 0) return;

      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      
      this.renderer.setSize(width, height, false);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      
      this.renderer.render(this.scene, this.camera);
    }
  }

  private async setupData() {
    const id = this.tirId();
    if (!id) return;

    // Le modèle géologique repose sur les trajectoires et longueurs des trous : à chaque
    // (re)chargement — changement de tir, retour d'import — il part et revient avec les nouveaux trous.
    const seq = ++this.setupSeq;
    this.holesReady.set(false);
    this.clearGeology();
    this.holePaths.clear();

    try {
      const blast = await this.holeService.GetBlastHolePositions(id);
      // Un chargement plus récent (autre tir, autre import) a pris la main : sa réponse fait foi.
      if (seq !== this.setupSeq) return;

      this.setupHoles(blast);
      this.holesReady.set(true);
      // Store last blast data for lookup
      this.lastBlast = blast;

      // Load terrain data after holes are set up (so offsetPoint is calculated)
      const hasNoHoles = !blast.Holes || blast.Holes.length === 0;
      this.loadTerrain(hasNoHoles);
    } catch (error) {
      console.error('Failed to set up 3D blast data:', error);
    }
  }

  /**
   * Load terrain protobuf data from API and add to scene
   */
  private loadTerrain(centerCamera = false): void {
    if (!this.tirId()) {
      console.warn('Cannot load terrain: tirId is not set');
      return;
    }

    // Remove existing terrain before loading new one
    this.clearTerrain();

    this.isLoadingTerrain.set(true);
    this.terrainLoadError.set(null);

    this.httpService.getTerrainBinary(this.tirId()).subscribe({
      next: async (arrayBuffer: ArrayBuffer) => {
        try {
          console.log('Terrain binary data received, size:', arrayBuffer.byteLength);
          console.log('Current offsetPoint for alignment:', this.offsetPoint);

          // Parse protobuf and create Three.js meshes
          // Pass offsetPoint to apply it directly to vertices (like OBJ loader does)
          //
          // L'origine vient du premier trou. Sur un tir sans trou rien ne la fixe, et le
          // maillage resterait en coordonnées projetées absolues : précision du float32
          // réduite au décimètre, et géométrie à des centaines de milliers d'unités de la
          // cible des contrôles, d'où un terrain qui saute dès qu'on tourne la caméra.
          // Dans ce cas on laisse le terrain fournir sa propre origine.
          const imposedOrigin = this.sceneOrigin();

          const terrainResult: TerrainLoadResult = await this.terrainLoaderService.loadTerrainFromBinary(
            arrayBuffer,
            imposedOrigin,
            true
          );

          // La scène entière doit partager le repère retenu, sinon le cadrage vise à côté.
          if (!imposedOrigin) {
            this.offsetPoint = new THREE.Vector3(
              terrainResult.offset.x,
              terrainResult.offset.y,
              terrainResult.offset.z,
            );
            this.frameIsFixed = terrainResult.terrains.children.length > 0;
          }

          console.log('Terrain loaded successfully:', {
            terrainsCount: terrainResult.terrains.children.length,
            offset: terrainResult.offset,
            bounds: terrainResult.bounds
          });

          // Debug: Log mesh details
          terrainResult.terrains.children.forEach((child, index) => {
            const mesh = child as THREE.Mesh;
            console.log(`Terrain ${index}:`, {
              type: child.type,
              visible: child.visible,
              frustumCulled: mesh.frustumCulled,
              geometry: mesh.geometry,
              material: mesh.material,
              vertexCount: mesh.geometry?.attributes?.position?.count,
              boundingBox: mesh.geometry?.boundingBox,
              boundingSphere: mesh.geometry?.boundingSphere
            });
          });

          // No need to apply position offset - it's already applied to vertices
          // The terrain vertices are now in the same coordinate space as the holes

          // Double-check frustum culling is disabled (already done in service)
          terrainResult.terrains.traverse((child) => {
            if (child instanceof THREE.Mesh) {
              child.frustumCulled = false;
              // Also ensure the mesh updates its matrix
              child.updateMatrix();
              child.updateMatrixWorld(true);
            }
          });

          // Check if terrain has actual mesh data
          if (terrainResult.terrains.children.length === 0) {
            console.log('Terrain data is empty');
            // Rien à afficher : la liste doit rester vide plutôt que de garder l'état précédent
            this.clearTerrain();
            this.isLoadingTerrain.set(false);
          } else {
            // Add terrain to scene
            this.scene.add(terrainResult.terrains);
            this.loadedTerrain = terrainResult.terrains;
            this.refreshTerrainLayers();
          }

          // Le terrain arrive de façon asynchrone, bien après le rendu déclenché au montage.
          // Sans ce redessin il reste invisible jusqu'au premier mouvement de caméra.
          this.renderer?.render(this.scene, this.camera);

          // Always load blast lines (crest and foot)
          this.loadBlastLines();

          // Expose scene and camera globally for debugging
          (window as any).scene = this.scene;
          (window as any).camera = this.camera;
          (window as any).terrain = terrainResult.terrains;

          console.log('Terrain added to scene. Scene children count:', this.scene.children.length);
          console.log('DEBUG: You can now inspect terrain in console using window.terrain, window.scene, window.camera');

          this.isLoadingTerrain.set(false);

          // Center camera after terrain is loaded if no holes
          if (centerCamera) {
            this.centerCameraOnScene();
          }
        } catch (error) {
          console.error('Failed to parse terrain data:', error);
          this.terrainLoadError.set('Failed to parse terrain data: ' + error);
          this.isLoadingTerrain.set(false);

          // Load blast lines when terrain parsing fails
          this.loadBlastLines();
        }
      },
      error: (error) => {
        console.error('Failed to load terrain from API:', error);
        this.terrainLoadError.set('Failed to load terrain from API: ' + error.message);
        this.isLoadingTerrain.set(false);

        // Load blast lines when there's no terrain
        this.loadBlastLines();
      }
    });
  }

  /**
   * Initialize protobuf for LineProto parsing
   */
  private async initializeLineProtobuf(): Promise<void> {
    if (this.lineProtoType) return;

    try {
      const root = await protobuf.load('assets/proto/geographic.proto');
      this.lineProtoType = root.lookupType('epc.vertex.LineProto');
    } catch (error) {
      console.error('Failed to load geographic.proto:', error);
    }
  }

  /**
   * Load blast lines (crest and foot) when there's no terrain
   */
  private async loadBlastLines(): Promise<void> {
    if (!this.tirId()) {
      console.warn('Cannot load blast lines: tirId is not set');
      return;
    }

    // Remove existing blast lines
    if (this.blastLinesGroup) {
      this.scene.remove(this.blastLinesGroup);
      this.blastLinesGroup = null;
    }

    // Initialize protobuf if needed
    await this.initializeLineProtobuf();
    if (!this.lineProtoType) {
      console.error('LineProto type not initialized');
      return;
    }

    this.httpService.getBlastLines(this.tirId()).subscribe({
      next: (lines: BlastLineViewDto[]) => {
        if (!lines || lines.length === 0) {
          console.log('No blast lines data available');
          return;
        }

        console.log('Blast lines received:', lines.length);
        this.renderBlastLines(lines);
      },
      error: (error) => {
        console.error('Failed to load blast lines:', error);
      }
    });
  }

  /**
   * Render blast lines (crest and foot) in 3D
   */
  private renderBlastLines(lines: BlastLineViewDto[]): void {
    this.blastLinesGroup = new THREE.Group();
    this.blastLinesGroup.name = 'BlastLines';

    // Define colors for different line types
    const crestLineColor = 0x3B76E3; // Blue for crest
    const footLineColor = 0x136731;  // Green for foot

    for (const lineDto of lines) {
      // Only render crest and foot lines
      if (lineDto.lineType !== BlastLineTypeProto.CrestLine &&
          lineDto.lineType !== BlastLineTypeProto.FootLine) {
        continue;
      }

      if (!lineDto.lineData) {
        continue;
      }

      try {
        let linePoints: Array<{ x: number; y: number; z: number }> = [];

        // First, try to parse as JSON (C# might serialize as JSON)
        try {
          const jsonData = JSON.parse(lineDto.lineData);
          // Handle different JSON structures
          if (jsonData.point && Array.isArray(jsonData.point)) {
            linePoints = jsonData.point;
          } else if (jsonData.Point && Array.isArray(jsonData.Point)) {
            linePoints = jsonData.Point;
          } else if (Array.isArray(jsonData)) {
            linePoints = jsonData;
          }
        } catch {
          // If JSON parsing fails, try protobuf (data is hex-encoded)
          // Check for empty or too short strings before parsing
          if (this.lineProtoType && lineDto.lineData && lineDto.lineData.length >= 4) {
            // Decode hex string to bytes
            const bytes = this.hexToBytes(lineDto.lineData);

            const message = this.lineProtoType.decode(bytes);
            const lineProto = this.lineProtoType.toObject(message, {
              longs: Number,
              enums: String,
              defaults: true,
              arrays: true
            });

            if (lineProto.point) {
              linePoints = lineProto.point;
            }
          }
        }

        if (linePoints.length < 2) {
          continue;
        }

        // Create Three.js line
        const points: THREE.Vector3[] = linePoints.map((pt: any) =>
          new THREE.Vector3(
            pt.x - this.offsetPoint.x,
            pt.y - this.offsetPoint.y,
            pt.z - this.offsetPoint.z
          )
        );

        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const color = lineDto.lineType === BlastLineTypeProto.CrestLine ? crestLineColor : footLineColor;
        const material = new THREE.LineBasicMaterial({ color: color });

        const line = new THREE.Line(geometry, material);
        line.userData = {
          type: lineDto.lineType === BlastLineTypeProto.CrestLine ? 'crest' : 'foot',
          lineType: lineDto.lineType
        };

        this.blastLinesGroup.add(line);

        console.log(`Added ${lineDto.lineType === BlastLineTypeProto.CrestLine ? 'crest' : 'foot'} line with ${points.length} points`);
      } catch (error) {
        console.error('Failed to parse line data:', error, 'Raw data:', lineDto.lineData.substring(0, 100));
      }
    }

    if (this.blastLinesGroup.children.length > 0) {
      this.scene.add(this.blastLinesGroup);
      console.log('Blast lines added to scene:', this.blastLinesGroup.children.length);
    }
  }

  /**
   * Convert hex string to Uint8Array
   */
  private hexToBytes(hex: string): Uint8Array {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
    }
    return bytes;
  }

  /**
   * Remove blast lines from scene
   */
  public removeBlastLines(): void {
    if (this.blastLinesGroup) {
      this.scene.remove(this.blastLinesGroup);
      this.blastLinesGroup = null;
    }
  }

  /**
   * Détache le terrain de la scène et vide la liste qui le décrit.
   *
   * Point de passage unique : la liste et le groupe doivent disparaître ensemble, sans quoi le
   * panneau continue d'afficher des versions dont les maillages ne sont plus dans la scène.
   */
  private clearTerrain(): void {
    if (this.loadedTerrain) {
      this.scene.remove(this.loadedTerrain);
      this.loadedTerrain = null;
    }
    this.terrainLayers.set([]);
  }

  /**
   * Remove terrain from scene
   */
  public removeTerrain(): void {
    this.clearTerrain();
  }

  /**
   * Toggle terrain visibility
   */
  public toggleTerrainVisibility(): void {
    if (this.loadedTerrain) {
      this.loadedTerrain.visible = !this.loadedTerrain.visible;
    }
  }

  // ── Modèle géologique ─────────────────────────────────────────────────────

  /**
   * Affiche le modèle géologique posé par la légende : chaque trou relevé occupe sa cellule de la
   * vue de dessus, empilée en tranches colorées selon ses lithologies ou ses drill logs
   * (geology-model.ts). Les blocs ne sont recalculés que si les tranches ou les cellules changent ;
   * masquer une catégorie ou en changer la couleur ne fait que redessiner.
   */
  private renderGeology(model: GeologyModel | null): void {
    if (!model) {
      this.clearGeology();
      return;
    }

    if (model.holes !== this.geologyHoles || model.cells !== this.geologyCells) {
      this.geologyTriangles.clear();
      this.geologyEdgeOwners.clear();
      // Une colonne par cellule : des trous qui partagent une tête partagent leurs anneaux de cellule
      // (hole-cells.ts), et la remplissent ensemble au lieu d'empiler deux colonnes au même endroit.
      const columns = new Map<readonly Position[][], { holes: HoleTrack[]; rings: THREE.Vector2[][] }>();
      for (const [holeId, intervals] of model.holes) {
        const hole = this.holePaths.get(holeId);
        const cell = model.cells.get(holeId);
        if (!hole || !cell || !intervals.length) continue;
        let column = columns.get(cell.rings);
        if (!column) {
          column = { holes: [], rings: this.cellToScene(cell.rings) };
          columns.set(cell.rings, column);
        }
        column.holes.push({ path: hole.path, length: hole.length, intervals });
      }
      this.geologyHoles = model.holes;
      this.geologyCells = model.cells;
      this.geologyBlocks = buildBlocks(columns.values(), model.kind).sort((a, b) => a.bottom - b.bottom);

      const blocks = this.geologyBlocks;
      this.geologyBottom = blocks.length ? blocks[0].bottom : 0;
      this.geologyThickness = blocks.reduce((top, block) => Math.max(top, block.top), this.geologyBottom) - this.geologyBottom;
      this.geologyPercent.set(100);
    }

    this.geologyColors = model.colors;
    this.geologyHidden = model.hidden;
    this.geologyKind.set(this.geologyBlocks.length ? model.kind : null);
    this.drawGeology();
  }

  /**
   * Dessine les blocs visibles : catégorie affichée, base sous le seuil du curseur (du fond vers la
   * surface). Refait à chaque cran, car la coupe du curseur crée de nouvelles faces extérieures.
   */
  private drawGeology(): void {
    this.removeGeologyMesh();
    const threshold = this.geologyThickness * this.geologyPercent() / 100;
    const shown = this.geologyBlocks.filter(block =>
      !this.geologyHidden.has(block.id) && block.bottom - this.geologyBottom < threshold);
    if (!shown.length) return;

    this.geologyGroup = this.createGeologyGroup(shown, this.geologyColors);
    this.scene.add(this.geologyGroup);
  }

  /**
   * Toutes les faces dans une seule géométrie, tous les contours dans une autre : un tir compte
   * facilement des milliers de blocs. Seules les faces extérieures du volume sont dessinées
   * (exteriorFaces) pour que la transparence ne s'empile pas d'un bloc à l'autre.
   */
  private createGeologyGroup(blocks: GeologyBlock[], colors: ReadonlyMap<number, string>): THREE.Group {
    const exterior = exteriorFaces(blocks, this.geologyEdgeOwners);

    // Les matériaux de la scène ne sont pas éclairés : un ombrage par face (dessus clair, dessous
    // sombre, côtés selon leur orientation) donne du relief à un bloc d'une seule couleur.
    const facePositions: number[] = [];
    const faceColors: number[] = [];
    const edgePositions: number[] = [];
    const color = new THREE.Color();
    const addFace = (point: Vec2, z: number, shade: number): void => {
      facePositions.push(point.x, point.y, z);
      faceColors.push(color.r * shade, color.g * shade, color.b * shade);
    };
    const addEdge = (from: Vec2, to: Vec2, z: number): void => {
      edgePositions.push(from.x, from.y, z, to.x, to.y, z);
    };

    blocks.forEach((block, blockIndex) => {
      color.set(normalizeHexColor(colors.get(block.id), GEOLOGY_FALLBACK_COLOR));
      const { top, bottom, walls } = exterior[blockIndex];
      block.rings.forEach((ring, ringIndex) => {
        // Dessus vers le haut, dessous vers le bas (anneau et triangles en sens trigonométrique).
        for (const [a, b, c] of this.geologyTriangles.get(ring) ?? []) {
          if (top) {
            addFace(ring[a], block.top, 1);
            addFace(ring[b], block.top, 1);
            addFace(ring[c], block.top, 1);
          }
          if (bottom) {
            addFace(ring[a], block.bottom, 0.5);
            addFace(ring[c], block.bottom, 0.5);
            addFace(ring[b], block.bottom, 0.5);
          }
        }
        // Côtés tournés vers l'extérieur, sur la hauteur que la cellule voisine ne couvre pas ; le
        // haut de chaque face visible est tracé, comme les contours de la vue de dessus et les
        // limites entre tranches sur les flancs.
        ring.forEach((from, index) => {
          const to = ring[(index + 1) % ring.length];
          const spans = walls[ringIndex][index];
          const shade = 0.65 + 0.15 * Math.abs(to.y - from.y) / (Math.hypot(to.x - from.x, to.y - from.y) || 1);
          for (const [low, high] of spans) {
            addFace(from, low, shade);
            addFace(to, low, shade);
            addFace(to, high, shade);
            addFace(from, low, shade);
            addFace(to, high, shade);
            addFace(from, high, shade);
          }
          if (top || spans.some(([, high]) => high === block.top)) addEdge(from, to, block.top);
        });
      });
    });

    const faceGeometry = new THREE.BufferGeometry();
    faceGeometry.setAttribute('position', new THREE.Float32BufferAttribute(facePositions, 3));
    faceGeometry.setAttribute('color', new THREE.Float32BufferAttribute(faceColors, 3));
    // Légèrement transparents : on devine les trous et les charges derrière une face. Pour les voir
    // à l'intérieur du volume, le curseur coupe le modèle et la légende en masque des couches.
    const faces = new THREE.Mesh(faceGeometry, new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.85 }));

    const edgeGeometry = new THREE.BufferGeometry();
    edgeGeometry.setAttribute('position', new THREE.Float32BufferAttribute(edgePositions, 3));
    const edges = new THREE.LineSegments(edgeGeometry, new THREE.LineBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.25 }));

    const group = new THREE.Group();
    group.add(faces, edges);
    return group;
  }

  /**
   * Anneaux d'une cellule dans le repère de la scène, avec le dessus triangulé et les arêtes
   * enregistrées pour retrouver les cellules voisines (exteriorFaces).
   */
  private cellToScene(cellRings: readonly Position[][]): THREE.Vector2[][] {
    const rings = cellRings.map(ring => this.toSceneRing(ring)).filter(ring => ring.length >= 3);
    for (const ring of rings) {
      this.geologyTriangles.set(ring, this.triangulateTop(ring));
      ring.forEach((from, index) => {
        const key = edgeKey(from, ring[(index + 1) % ring.length]);
        const owners = this.geologyEdgeOwners.get(key) ?? new Set<GeologyBlock['rings']>();
        owners.add(rings);
        this.geologyEdgeOwners.set(key, owners);
      });
    }
    return rings;
  }

  /**
   * Cellule de la vue de dessus (projection du site) dans le repère de la scène : sans le point
   * de fermeture, et en sens trigonométrique pour que les faces latérales regardent vers l'extérieur.
   */
  private toSceneRing(ring: readonly Position[]): THREE.Vector2[] {
    const points = ring.map(([x, y]) => new THREE.Vector2(x - this.offsetPoint.x, y - this.offsetPoint.y));
    if (points.length > 1 && points[0].equals(points[points.length - 1])) points.pop();
    return THREE.ShapeUtils.isClockWise(points) ? points.reverse() : points;
  }

  /** Triangles du dessus d'une cellule, tous en sens trigonométrique (vus d'en haut). */
  private triangulateTop(ring: THREE.Vector2[]): number[][] {
    return THREE.ShapeUtils.triangulateShape(ring, []).map(([a, b, c]) => {
      const cross = (ring[b].x - ring[a].x) * (ring[c].y - ring[a].y) - (ring[b].y - ring[a].y) * (ring[c].x - ring[a].x);
      return cross < 0 ? [a, c, b] : [a, b, c];
    });
  }

  /**
   * Curseur : le modèle se construit du fond vers la surface. À p %, seuls les blocs dont la base
   * se trouve dans les p % inférieurs de l'épaisseur sont dessinés.
   */
  onGeologyPercentChange(value: number | string): void {
    const percent = Number(value);
    if (percent === this.geologyPercent()) return;
    this.geologyPercent.set(percent);
    if (this.geologyBlocks.length) this.drawGeology();
  }

  /** Retire le modèle et oublie ses blocs : il sera recalculé au prochain modèle posé. */
  private clearGeology(): void {
    this.removeGeologyMesh();
    this.geologyHoles = null;
    this.geologyCells = null;
    this.geologyBlocks = [];
    this.geologyTriangles.clear();
    this.geologyEdgeOwners.clear();
    this.geologyColors = new Map();
    this.geologyHidden = new Set();
    this.geologyBottom = 0;
    this.geologyThickness = 0;
    this.geologyKind.set(null);
  }

  private removeGeologyMesh(): void {
    if (this.geologyGroup) {
      this.scene?.remove(this.geologyGroup);
      for (const child of this.geologyGroup.children as (THREE.Mesh | THREE.LineSegments)[]) {
        child.geometry.dispose();
        (child.material as THREE.Material).dispose();
      }
      this.geologyGroup = null;
    }
  }

  private setupHoles(blast: Blast): void {
    const topMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });
    const bottomMaterial = new THREE.MeshBasicMaterial({ color: 0x0000ff });
    const sphereGeometry = new THREE.SphereGeometry(0.25, 16, 16);
    const cylinderGeometry = new THREE.CylinderGeometry(0.20, 0.20, 1, 16, 1, true);
    const lineMaterial = new THREE.LineBasicMaterial({ color: 0x000000 });
    const deviationLineMaterial = new THREE.LineBasicMaterial({ color: 0x00ff00, linewidth: 2 }); // Vert pour la déviation
    const deviationMarkerMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00 }); // Vert pour les marqueurs

    this.setOffsetPoint(blast.Holes);

  blast.Holes.forEach((hole: any) => {
    if (!hole.Geometry) return; // Skip if no geometry data

    const topPoint = this.calculateTopPoint(hole.PositionPlannedXYZ);
    // Trajectoire gardée pour le modèle géologique même quand le trou n'est pas dessiné en entier
    // (effondré, manquant) : ce qu'il a relevé avant reste de la géologie du tir.
    this.holePaths.set(hole.publicGuid, this.trajectoryOf(hole.Geometry, topPoint));

    const status = hole.Status?.toUpperCase();
    if (status === 'MISSING' || status === 'COLLAPSED') {
      this.addHoleHead(hole, sphereGeometry, topMaterial, topPoint);
      return;
    }

    if (hole.Geometry.Deviation && hole.Geometry.Deviation.segments && hole.Geometry.Deviation.segments.length > 0) {
      this.addHoleWithDeviation(hole, topPoint, sphereGeometry, topMaterial, bottomMaterial, lineMaterial, cylinderGeometry);
      this.addChargeElementsWithDeviation(hole, topPoint, cylinderGeometry);
      this.addDeviationPath(hole, deviationLineMaterial, deviationMarkerMaterial);
    } else {
      const holeUnitVector = this.calculateHoleUnitVector(hole.Geometry);
      const bottomPoint = this.calculateBottomPoint(topPoint, holeUnitVector, hole.Geometry.length);
      const cylinderQuaternion = this.calculateCylinderQuaternion(holeUnitVector);
      this.addHoleHead(hole, sphereGeometry, topMaterial, topPoint);
      this.addMesh(sphereGeometry, bottomMaterial, bottomPoint);
      this.addConnectingLine(lineMaterial, topPoint, bottomPoint);
      this.addChargeElements(hole, bottomPoint, holeUnitVector, cylinderGeometry, cylinderQuaternion);
    }
  });
}

/**
 * Trajectoire d'un trou dans la scène, la même que pour ses charges : la tête puis les points de
 * la déviation, ou la tête et le pied d'un trou droit ; avec sa longueur déclarée, à laquelle se
 * rapportent les profondeurs relevées.
 */
private trajectoryOf(geometry: HoleGeometryDto, topPoint: THREE.Vector3): { path: THREE.Vector3[]; length: number } {
    const segments = geometry.Deviation?.segments;
    const path = segments?.length
      ? [topPoint, ...segments.map(segment => new THREE.Vector3(topPoint.x + segment.x, topPoint.y + segment.y, topPoint.z + segment.z))]
      : [topPoint, this.calculateBottomPoint(topPoint, this.calculateHoleUnitVector(geometry), geometry.length)];
    return { path, length: geometry.length };
}

private setOffsetPoint(holes: any[]): void {
    if (holes.length > 0) {
        const firstHole = holes[0].PositionMesuredXYZ || holes[0].PositionPlannedXYZ;
        this.offsetPoint = new THREE.Vector3(firstHole.x, firstHole.y, firstHole.z);
    } else {
        this.offsetPoint = new THREE.Vector3(0, 0, 0);
    }
    this.frameIsFixed = holes.length > 0;
}

/**
 * Origine à imposer à une géométrie qui arrive dans la scène (terrain, OBJ) : celle de la scène
 * si un élément l'a déjà fixée, sinon aucune — la géométrie fournit alors la sienne, que la
 * scène adopte.
 */
private sceneOrigin(): THREE.Vector3 | undefined {
    return this.frameIsFixed ? this.offsetPoint : undefined;
}

private calculateTopPoint(position: any): THREE.Vector3 {
    return new THREE.Vector3(
        position.x - this.offsetPoint.x,
        position.y - this.offsetPoint.y,
        position.z - this.offsetPoint.z
    );
}

private calculateHoleUnitVector(geometry: any): THREE.Vector3 {
    const v = new THREE.Vector3(0, 0, -1);
    v.applyAxisAngle(new THREE.Vector3(1, 0, 0), geometry.angle);
    v.applyAxisAngle(new THREE.Vector3(0, 0, -1), geometry.azimuth);
    return v.normalize();
}

private calculateBottomPoint(topPoint: THREE.Vector3, unitVector: THREE.Vector3, length: number): THREE.Vector3 {
    return topPoint.clone().add(unitVector.clone().multiplyScalar(length));
}

private calculateCylinderQuaternion(unitVector: THREE.Vector3): THREE.Quaternion {
    const quaternion = new THREE.Quaternion();
    return quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), unitVector);
}

private addMesh(geometry: THREE.SphereGeometry, material: THREE.MeshBasicMaterial, position: THREE.Vector3): void {
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.copy(position);
    this.scene.add(mesh);
}

/**
 * Pose la tête d'un trou et la garde cliquable. Identique à addMesh, mais l'association
 * au trou permet au clic de retrouver lequel a été touché, quel que soit son état.
 */
private addHoleHead(hole: any, geometry: THREE.SphereGeometry, material: THREE.MeshBasicMaterial, position: THREE.Vector3): void {
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.copy(position);
    this.scene.add(mesh);
    this.holeHeadMeshes.push({ mesh, hole });
}

private addConnectingLine(material: THREE.LineBasicMaterial, start: THREE.Vector3, end: THREE.Vector3): void {
    const points = [start, end];
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const line = new THREE.Line(geometry, material);
    this.scene.add(line);
}

private addChargeElements(
    hole: Hole,
    bottomPoint: THREE.Vector3,
    holeUnitVector: THREE.Vector3,
    cylinderGeometry: THREE.CylinderGeometry,
    cylinderQuaternion: THREE.Quaternion
): void {
    let offset = bottomPoint.clone();
    const chargeElements = hole.Loading?.actualCharge?.chargeElements || hole.Loading?.plannedCharge?.chargeElements || [];
    let currentDepth = hole.Geometry.length;

    chargeElements.forEach((charge: Charges) => {
        const chargeLength = Math.abs(charge.depth - currentDepth);
        const colorFromId = this.numberToHexColor(charge.productId);
        const material = new THREE.MeshBasicMaterial({ color: colorFromId });
        const cylinder = new THREE.Mesh(cylinderGeometry, material);

        const halfLength = chargeLength / 2.0;
        cylinder.position.copy(offset.add(holeUnitVector.clone().multiplyScalar(-halfLength)));
        cylinder.scale.set(1, chargeLength, 1);
        cylinder.applyQuaternion(cylinderQuaternion);
        this.scene.add(cylinder);

        const { productName, isBulk } = this.resolveProductInfo(charge);
        this.chargeMeshes.push({ mesh: cylinder, charge: { ...charge, holeName: hole.name, productName, isBulk }, originalColor: colorFromId });

        currentDepth = charge.depth;
        offset.add(holeUnitVector.clone().multiplyScalar(-halfLength));
    });
}




private addHoleWithDeviation(
    hole: any, 
    topPoint: THREE.Vector3, 
    sphereGeometry: THREE.SphereGeometry, 
    topMaterial: THREE.MeshBasicMaterial, 
    bottomMaterial: THREE.MeshBasicMaterial, 
    lineMaterial: THREE.LineBasicMaterial, 
    cylinderGeometry: THREE.CylinderGeometry
): void {
    const deviationSegments = hole.Geometry.Deviation.segments;
    
    // Calculate all points along the deviation path
    const deviationPoints: THREE.Vector3[] = deviationSegments.map((segment: any) => 
        new THREE.Vector3(
            topPoint.x + segment.x,
            topPoint.y + segment.y,
            topPoint.z + segment.z
        )
    );
    
    const allPoints = [topPoint, ...deviationPoints];
    
    this.addHoleHead(hole, sphereGeometry, topMaterial, topPoint);
    if (allPoints.length > 1) {
        this.addMesh(sphereGeometry, bottomMaterial, allPoints.at(-1));
    }
    
}



private getPositionAlongPath(points: THREE.Vector3[], lengths: number[], targetDistance: number): THREE.Vector3 | null {
    if (targetDistance <= 0) return points[0];
    if (targetDistance >= lengths.at(-1)) return points.at(-1);
    
    let left = 0;
    let right = lengths.length - 1;
    
    while (left < right - 1) {
        const mid = Math.floor((left + right) / 2);
        if (lengths[mid] <= targetDistance) {
            left = mid;
        } else {
            right = mid;
        }
    }
    
    const segmentStart = lengths[left];
    const segmentEnd = lengths[right];
    const segmentRatio = (targetDistance - segmentStart) / (segmentEnd - segmentStart);
    
    // Optimized: Direct lerp without clone
    const result = new THREE.Vector3();
    result.lerpVectors(points[left], points[right], segmentRatio);
    return result;
}

private addChargeElementsWithDeviation(
    hole: any, 
    topPoint: THREE.Vector3, 
    cylinderGeometry: THREE.CylinderGeometry
): void {
    const chargeElements = hole.Loading?.actualCharge?.chargeElements || hole.Loading?.plannedCharge?.chargeElements || [];
    if (chargeElements.length === 0 || !hole.Geometry.Deviation?.segments) return;
    
    const deviationSegments = hole.Geometry.Deviation.segments;
    const deviationPoints: THREE.Vector3[] = deviationSegments.map((segment: any) => 
        new THREE.Vector3(
            topPoint.x + segment.x,
            topPoint.y + segment.y,
            topPoint.z + segment.z
        )
    );
    
    const allPoints = [topPoint, ...deviationPoints];
    
    const pathLengths: number[] = [0];
    for (let i = 1; i < allPoints.length; i++) {
        const segmentLength = allPoints[i - 1].distanceTo(allPoints[i]);
        pathLengths.push(pathLengths.at(-1) + segmentLength);
    }
    const totalLength = pathLengths.at(-1);
    
    const sortedCharges = [...chargeElements].sort((a, b) => b.depth - a.depth);
    
    let currentDepth = hole.Geometry.length;
    
    sortedCharges.forEach((charge: Charges) => {
        const chargeLength = Math.abs(charge.depth - currentDepth);
        
        if (chargeLength > 0.01) {
            const startDepthRatio = currentDepth / hole.Geometry.length;
            const endDepthRatio = charge.depth / hole.Geometry.length;
            
            const startDistance = startDepthRatio * totalLength;
            const endDistance = endDepthRatio * totalLength;
            
            this.createChargeSegmentsAlongPath(allPoints, pathLengths, startDistance, endDistance, charge, hole.name);
        }
        
        currentDepth = charge.depth;
    });
}

private createChargeSegmentsAlongPath(
    allPoints: THREE.Vector3[],
    pathLengths: number[],
    startDistance: number,
    endDistance: number,
    charge: Charges,
    holeName: string
): void {
    const colorFromId = this.numberToHexColor(charge.productId);
    const material = new THREE.MeshBasicMaterial({ color: colorFromId });
    
    const chargeDistance = Math.abs(endDistance - startDistance);
    const numSegments = Math.min(6, Math.max(3, Math.floor(chargeDistance * 5))); // Much fewer segments
    
    const segmentGeometry = new THREE.CylinderGeometry(0.18, 0.18, 1, 6, 1, true); // Lower resolution
    const up = new THREE.Vector3(0, 1, 0);
    
    const segmentStep = (endDistance - startDistance) / numSegments;

    // Une seule fiche pour tous les tronçons : c'est elle qui relie les cylindres d'une même
    // charge, et un clic sur l'un d'eux surligne alors la charge entière, pas un seul tronçon.
    const { productName, isBulk } = this.resolveProductInfo(charge);
    const chargeInfo: ChargesWithHoleName = { ...charge, holeName, productName, isBulk };

    for (let i = 0; i < numSegments; i++) {
        const segmentStartDistance = startDistance + i * segmentStep;
        const segmentEndDistance = startDistance + (i + 1) * segmentStep;
        
        const segmentStartPos = this.getPositionAlongPath(allPoints, pathLengths, segmentStartDistance);
        const segmentEndPos = this.getPositionAlongPath(allPoints, pathLengths, segmentEndDistance);
        
        if (segmentStartPos && segmentEndPos) {
            const segmentLength = segmentStartPos.distanceTo(segmentEndPos);
            
            if (segmentLength > 0.01) {
                const midPos = segmentStartPos.clone().add(segmentEndPos).multiplyScalar(0.5);
                const direction = segmentEndPos.clone().sub(segmentStartPos).normalize();
                
                const segmentCylinder = new THREE.Mesh(segmentGeometry, material);
                
                segmentCylinder.position.copy(midPos);
                segmentCylinder.scale.set(1, segmentLength, 1);
                segmentCylinder.quaternion.setFromUnitVectors(up, direction);
                
                this.scene.add(segmentCylinder);

                this.chargeMeshes.push({ mesh: segmentCylinder, charge: chargeInfo, originalColor: colorFromId });
            }
        }
    }
}

private addDeviationPath(hole: any, lineMaterial: THREE.LineBasicMaterial, markerMaterial: THREE.MeshBasicMaterial): void {
    if (!hole.Geometry.Deviation || !hole.Geometry.Deviation.segments || hole.Geometry.Deviation.segments.length === 0) {
        console.log(`No deviation data for hole ${hole.name}`);
        return;
    }

    const deviationSegments = hole.Geometry.Deviation.segments;
    console.log(`Adding deviation path for hole ${hole.name} with ${deviationSegments.length} segments`);

    const topPoint = this.calculateTopPoint(hole.PositionPlannedXYZ);
    
    const deviationPoints: THREE.Vector3[] = deviationSegments.map((segment: any) => 
        new THREE.Vector3(
            topPoint.x + segment.x,
            topPoint.y + segment.y,
            topPoint.z + segment.z
        )
    );

    const completeDeviationPath = [topPoint, ...deviationPoints];

    const geometry = new THREE.BufferGeometry().setFromPoints(completeDeviationPath);
    const deviationLine = new THREE.Line(geometry, lineMaterial);
    
    deviationLine.userData = { type: 'deviation', holeName: hole.name };
    this.scene.add(deviationLine);

    this.addDeviationMarkers(deviationPoints, markerMaterial, hole.name);
}

private addDeviationMarkers(points: THREE.Vector3[], material: THREE.MeshBasicMaterial, holeName: string): void {
    const markerGeometry = new THREE.SphereGeometry(0.1, 8, 8);

    points.forEach((point, index) => {
        const marker = new THREE.Mesh(markerGeometry, material);
        marker.position.copy(point);
        
        this.scene.add(marker);
    });
}

  private setupCamera(){
    this.camera.up.set(0, 0, 1);
    this.camera.position.z = 15;
    this.camera.position.y = 10;
    this.camera.lookAt(0, 0, 0);
  }

  private setupScene(){
    this.scene.background = new THREE.Color().setHSL( 0.6, 0, 0.3 );

    const axesHelper = new THREE.AxesHelper( 5 );
    this.scene.add( axesHelper );

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(10, 20, 10);
    directionalLight.castShadow = true;
    this.scene.add(directionalLight);

    const hemiLight = new THREE.HemisphereLight(0xffffff, 0x444444, 0.4);
    this.scene.add(hemiLight);
  }
  private setupControls(){
    this.controls = new OrbitControls( this.camera, this.renderer.domElement );
    this.controls.target.set( 0, 2, 0 );
    this.controls.update();
  }

  /**
   * Moves the camera and its orbit target together along the screen axes
   * (dx: +1 right / -1 left, dy: +1 up / -1 down), by a step proportional to the zoom level.
   */
  pan(dx: number, dy: number): void {
    if (!this.controls) return;
    const step   = this.camera.position.distanceTo(this.controls.target) * 0.1;
    const right  = new THREE.Vector3().setFromMatrixColumn(this.camera.matrix, 0).multiplyScalar(dx * step);
    const up     = new THREE.Vector3().setFromMatrixColumn(this.camera.matrix, 1).multiplyScalar(dy * step);
    const offset = right.add(up);
    this.camera.position.add(offset);
    this.controls.target.add(offset);
    this.controls.update();
  }

  /**
   * Center camera on terrain or OBJ models when there are no holes
   */
  private centerCameraOnScene(): void {
    const box = new THREE.Box3();

    // Include terrain in bounding box
    if (this.loadedTerrain) {
      box.expandByObject(this.loadedTerrain);
    }

    // Include OBJ models in bounding box
    this.loadedObjModels().forEach(model => {
      box.expandByObject(model);
    });

    // If we have something to center on
    if (!box.isEmpty()) {
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());

      // Calculate camera distance to fit the entire scene
      const maxDim = Math.max(size.x, size.y, size.z);
      const fov = this.camera.fov * (Math.PI / 180);
      let cameraDistance = Math.abs(maxDim / Math.sin(fov / 2)) * 1.5;

      // Position camera at an angle
      this.camera.position.set(
        center.x + cameraDistance * 0.5,
        center.y - cameraDistance * 0.5,
        center.z + cameraDistance * 0.8
      );

      this.camera.lookAt(center);

      // Les plans de coupe viennent de l'initialisation (0,1 / 1000) et ne suivaient pas la
      // scène. Un terrain de quelques centaines de mètres recule la caméra au-delà de 1000 :
      // le fond passait derrière le plan lointain et disparaissait par morceaux à la rotation.
      // Le tampon de profondeur étant logarithmique, un grand rapport near/far reste sain.
      this.camera.far = (cameraDistance + maxDim) * 4;
      this.camera.near = Math.max(this.camera.far / 100000, 0.01);
      this.camera.updateProjectionMatrix();

      // Update OrbitControls target
      if (this.controls) {
        this.controls.target.copy(center);
        // Sans borne, la molette éloigne la caméra au-delà du plan lointain qu'on vient
        // d'ajuster, et le terrain se remettrait à disparaître.
        this.controls.maxDistance = this.camera.far * 0.5;
        this.controls.update();
      }

      console.log('Camera centered on scene:', {
        center,
        size,
        cameraDistance,
        cameraPosition: this.camera.position
      });
    } else {
      console.warn('No objects to center camera on');
    }
  }


  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (!event.dataTransfer?.files) return;

    const objFiles: File[] = [];
    const mtlFiles: File[] = [];
    const textureFiles: File[] = [];

    for (let i = 0; i < event.dataTransfer.files.length; i++) {
      const file = event.dataTransfer.files[i];
      const fileName = file.name.toLowerCase();

      if (fileName.endsWith('.obj')) {
        objFiles.push(file);
      } else if (fileName.endsWith('.mtl')) {
        mtlFiles.push(file);
      } else if (this.isImageFile(fileName)) {
        textureFiles.push(file);
      }
    }

    if (objFiles.length > 0) {
      this.selectedObjFile = objFiles[0];
      this.selectedMtlFile = mtlFiles.length > 0 ? mtlFiles[0] : null;
      this.selectedTextureFiles = textureFiles;
      this.loadOBJModel();
    }
  }

  onFileSelected(event: any) {
    const selectedFiles: FileList = event.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    const objFiles: File[] = [];
    const mtlFiles: File[] = [];
    const textureFiles: File[] = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const fileName = file.name.toLowerCase();

      if (fileName.endsWith('.obj')) {
        objFiles.push(file);
      } else if (fileName.endsWith('.mtl')) {
        mtlFiles.push(file);
      } else if (this.isImageFile(fileName)) {
        textureFiles.push(file);
      }
    }

    if (objFiles.length > 0) {
      this.selectedObjFile = objFiles[0];
      this.selectedMtlFile = mtlFiles.length > 0 ? mtlFiles[0] : null;
      this.selectedTextureFiles = textureFiles;
      this.loadOBJModel();
    }
  }

  private isImageFile(filename: string): boolean {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.bmp', '.tga', '.tiff', '.webp'];
    const ext = filename.split('.').pop()?.toLowerCase();
    return ext ? imageExtensions.includes('.' + ext) : false;
  }

  triggerFileInput(): void {
    const fileInputEl = this.fileInput();
    if (fileInputEl?.nativeElement) {
      fileInputEl.nativeElement.click();
    }
  }

  private async loadOBJModel(): Promise<void> {
    if (!this.selectedObjFile) return;

    try {
      let result: ModelLoadResult;

      // Sur un tir sans trou ni terrain déjà affiché, rien ne fixe encore le repère : on laisse
      // alors le fichier fournir son origine, sans quoi ses sommets resteraient à des
      // coordonnées projetées absolues et sauteraient à chaque mouvement de caméra.
      const imposedOrigin = this.sceneOrigin();

      if (this.selectedMtlFile || this.selectedTextureFiles.length > 0) {
        result = await this.modelLoaderService.loadOBJWithMTLAndTextureFiles(
          this.selectedObjFile,
          this.selectedMtlFile,
          this.selectedTextureFiles,
          imposedOrigin
        );
      } else {
        result = await this.modelLoaderService.loadModelFromFile(
          this.selectedObjFile,
          imposedOrigin
        );
      }

      // La sauvegarde rajoute offsetPoint aux sommets : il doit valoir l'origine retranchée.
      if (!imposedOrigin) {
        this.offsetPoint = result.origin.clone();
        this.frameIsFixed = true;
      }


      const maxAnisotropy = this.renderer.capabilities.getMaxAnisotropy();
      result.model.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          if (child.material instanceof THREE.Material) {
            const material = child.material as THREE.MeshPhongMaterial;
            if (material.map) {
              material.map.anisotropy = maxAnisotropy;
              material.map.needsUpdate = true;
            }
          }
          child.renderOrder = 1;
        }
      });


      // Le parseur nomme tous les groupes 'OBJ_Group' : sans ce renommage, chaque version
      // enregistrée porterait le même libellé et la liste des terrains serait illisible.
      // Le nom part dans le proto à la sauvegarde et revient au rechargement.
      if (this.selectedObjFile) {
        result.model.name = this.selectedObjFile.name.replace(/\.[^.]+$/, '');
      }

      this.scene.add(result.model);
      this.loadedObjModels.update(models => [...models, result.model]);

      // Center camera on OBJ if no holes
      if (!this.lastBlast || !this.lastBlast.Holes || this.lastBlast.Holes.length === 0) {
        this.centerCameraOnScene();
      }

    } catch (error) {
      console.error('ERROR:', error);
      alert('Erreur: ' + error);
    }
  }

  clearLoadedOBJModels(): void {
    for (const model of this.loadedObjModels()) {
      this.scene.remove(model);
      model.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => mat.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
    }
    this.loadedObjModels.set([]);
  }

  onPointerDown(event: PointerEvent) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, this.camera);
    
    // Check for charge intersections
    const chargeMeshes = this.chargeMeshes.map(obj => obj.mesh);
    const chargeIntersects = raycaster.intersectObjects(chargeMeshes);

    if (chargeIntersects.length > 0) {
      this.selectedHoleInfo.set(null); // Hide hole info

      const mesh = chargeIntersects[0].object as THREE.Mesh;
      const found = this.chargeMeshes.find(obj => obj.mesh === mesh);
      if (found) {
        this.selectedChargeInfo.set(found.charge);
        this.highlightCharge(found.charge);
      }
      return; // Stop if a charge was clicked
    }

    // Enfin la tête du trou : elle est toujours présente, y compris sur un trou sondé ou
    // non chargé, qui n'offre sinon aucune prise au clic. Testée en dernier pour ne rien
    // changer au comportement des charges.
    const holeHeadIntersects = raycaster.intersectObjects(this.holeHeadMeshes.map(obj => obj.mesh));

    if (holeHeadIntersects.length > 0) {
      const mesh = holeHeadIntersects[0].object as THREE.Mesh;
      const found = this.holeHeadMeshes.find(obj => obj.mesh === mesh);
      if (found) {
        this.selectedChargeInfo.set(null);
        this.resetChargeHighlight();
        this.selectedHoleInfo.set(found.hole);
        return;
      }
    }

    // If nothing was clicked, clear selections
    this.selectedChargeInfo.set(null);
    this.selectedHoleInfo.set(null);
    this.resetChargeHighlight();
  }

  onPointerMove(event: MouseEvent) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, this.camera);
    const meshes = this.chargeMeshes.map(obj => obj.mesh)
      .concat(this.holeHeadMeshes.map(obj => obj.mesh));
    const intersects = raycaster.intersectObjects(meshes);
    if (intersects.length > 0) {
      this.renderer.domElement.style.cursor = 'pointer';
    } else {
      this.renderer.domElement.style.cursor = 'default';
    }
  }

  /**
   * Surligne tous les cylindres de la charge : un seul sur un trou droit, plusieurs tronçons sur
   * un trou dévié. On recolore le matériau existant plutôt que d'en créer un : chaque charge a
   * le sien, partagé par ses seuls tronçons, et les matériaux abandonnés à chaque clic restaient
   * alloués côté GPU.
   */
  private highlightCharge(charge: ChargesWithHoleName) {
    this.resetChargeHighlight();
    this.setChargeColor(charge, 0xffff00);
    this.selectedCharge = charge;
  }

  private resetChargeHighlight() {
    if (this.selectedCharge) {
      this.setChargeColor(this.selectedCharge);
      this.selectedCharge = null;
    }
  }

  /** Couleur donnée à tous les cylindres de la charge, ou leur couleur d'origine sans argument. */
  private setChargeColor(charge: ChargesWithHoleName, color?: THREE.ColorRepresentation) {
    this.chargeMeshes
      .filter(obj => obj.charge === charge)
      .forEach(obj => (obj.mesh.material as THREE.MeshBasicMaterial).color.set(color ?? obj.originalColor));
  }

  private resolveProductInfo(charge: Charges): { productName: string, isBulk: boolean } {
    if (charge.productId === 0 || charge.isStemming) {
      return { productName: 'Stemming', isBulk: false };
    }
    const product = this.productMap.get(charge.productId);
    return {
      productName: product?.name || null,
      isBulk: product?.productType === ProductType.Bulk
    };
  }

  // Helper to compute charge length for info panel.
  // Uses the hole's source chargeElements (sorted by descending depth) to avoid
  // issues with deviated holes where a single logical charge produces multiple meshes.
  getChargeLength(charge: ChargesWithHoleName): number | null {
    if (!charge.holeName) return null;

    const hole = this.lastBlast?.Holes?.find(h => h.name === charge.holeName);
    if (!hole?.Geometry || typeof hole.Geometry.length !== 'number') return null;

    const chargeElements = hole.Loading?.actualCharge?.chargeElements || hole.Loading?.plannedCharge?.chargeElements || [];
    if (chargeElements.length === 0) return null;

    // Sort by descending depth (bottom-first, matching rendering order)
    const sorted = [...chargeElements].sort((a, b) => b.depth - a.depth);

    const index = sorted.findIndex(c => c.indexFromBottom === charge.indexFromBottom);
    if (index === -1) return null;

    if (index === 0) {
      return Math.abs(hole.Geometry.length - sorted[0].depth);
    }

    return Math.abs(sorted[index].depth - sorted[index - 1].depth);
  }

  private numberToHexColor(num: number): string {

    const hash = num * 2654435761 % Math.pow(2, 32);
  
    // Extract RGB components from the hash
    const r = (hash & 0xFF0000) >> 16; // Red component
    const g = (hash & 0x00FF00) >> 8;  // Green component
    const b = hash & 0x0000FF;         // Blue component
  
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }

  /**
   * Recharge les trous du tir, par exemple après un import lancé depuis
   * la barre d'outils de l'onglet Holes.
   */
  reload(): void {
    void this.setupData();
  }

  /**
   * Reconstruit la liste des terrains affichables depuis la scène. Le nom vient du proto ;
   * à défaut, on numérote pour que chaque entrée reste identifiable.
   */
  private refreshTerrainLayers(): void {
    const children = this.loadedTerrain?.children ?? [];
    this.terrainLayers.set(
      children.map((child, index) => ({
        name: this.terrainLayerName(child.name, index),
        visible: child.visible,
        // Posé par le chargeur ; il diffère de la position quand un terrain illisible a été sauté.
        protoIndex: typeof child.userData['protoIndex'] === 'number' ? child.userData['protoIndex'] : index,
      })),
    );
  }

  /**
   * Libellé d'une version dans le panneau.
   *
   * Le parseur OBJ nomme tous les groupes 'OBJ_Group' : les terrains enregistrés avant que
   * l'import ne reprenne le nom du fichier portent donc ce libellé, identique pour chacun.
   * On les numérote plutôt que d'afficher plusieurs fois la même chose.
   */
  private terrainLayerName(name: string, index: number): string {
    const PARSER_PLACEHOLDER = 'OBJ_Group';
    return !name || name === PARSER_PLACEHOLDER ? `Terrain ${index + 1}` : name;
  }

  /** Affiche ou masque un terrain sans toucher aux autres. */
  protected toggleTerrainLayer(index: number): void {
    const child = this.loadedTerrain?.children[index];
    if (!child) {
      return;
    }

    child.visible = !child.visible;
    this.refreshTerrainLayers();

    // Le rendu est à la demande : sans cet appel, la bascule ne serait visible
    // qu'au prochain déplacement de la caméra.
    this.renderer?.render(this.scene, this.camera);
  }

  /**
   * Demande d'abord quoi faire d'un terrain déjà enregistré : ajouter une version, ou en
   * remplacer une. Sans terrain existant, la question n'a pas lieu d'être et on enregistre.
   */
  saveTerrain(): void {
    if (this.loadedObjModels().length === 0 || !this.tirId()) {
      console.warn('Cannot save terrain: no OBJ models loaded or tirId not set');
      return;
    }

    const layers = this.terrainLayers();
    const existingVersions = layers.map(layer => layer.name);
    if (existingVersions.length === 0) {
      void this.persistTerrain(null);
      return;
    }

    const modal = this.modalService.show(SaveTerrainModalComponent, {
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: false,
      animated: false,
      class: 'modal-dialog-centered',
      initialState: { existingVersions },
    });

    // La popup désigne une version par sa place dans la liste ; le serveur, par son rang dans le
    // payload stocké.
    (modal.content as SaveTerrainModalComponent).onConfirm.subscribe((choice: SaveTerrainChoice) => {
      void this.persistTerrain(choice.replaceIndex === null ? null : layers[choice.replaceIndex].protoIndex);
    });
  }

  /** @param replaceIndex Rang de la version à écraser, ou null pour ajouter à la liste. */
  private async persistTerrain(replaceIndex: number | null): Promise<void> {
    this.isSavingTerrain.set(true);

    try {
      // Serialize OBJ models to protobuf binary format (include texture files)
      const arrayBuffer = await this.terrainLoaderService.serializeTerrainsToBinary(
        this.loadedObjModels(),
        this.offsetPoint,
        this.tirId(),
        this.utilisateurService.currentSite()?.siteId || 0,
        this.selectedTextureFiles
      );

      this.httpService.saveTerrainBinary(this.tirId(), arrayBuffer, replaceIndex).subscribe({
        next: () => {
          console.log('Terrain saved successfully');
          this.isSavingTerrain.set(false);
          this.fonctionsService.launch_toast(true, false);
          // Les OBJ importés viennent d'être persistés : ils reviendront par le terrain rechargé.
          // Les garder ici les afficherait en double et laisserait le bouton actif, prêt à
          // renvoyer une deuxième fois la même version.
          this.clearLoadedOBJModels();

          // La liste stockée a changé : on la relit plutôt que de la deviner côté client.
          // loadTerrain plutôt qu'updateScene : seul le terrain a bougé, inutile de reconstruire
          // toute la scène ni de perdre la position de caméra.
          this.loadTerrain();
        },
        error: (error) => {
          console.error('Failed to save terrain:', error);
          this.isSavingTerrain.set(false);
          this.fonctionsService.launch_toast(false, false, error.message || 'Erreur lors de l\'enregistrement du terrain');
        }
      });
    } catch (error) {
      console.error('Failed to serialize terrain:', error);
      this.isSavingTerrain.set(false);
      this.fonctionsService.launch_toast(false, false, 'Erreur lors de la sérialisation du terrain');
    }
  }

  openExportModal() {
    const exportModal = this.modalService.show(ExportFileModalComponent, { 
      backdrop: true, 
      ignoreBackdropClick: true, 
      keyboard: false,
      animated: false,
      class: 'modal-dialog-centered' 
    });

    const modalComponent = exportModal.content as ExportFileModalComponent;
    modalComponent.onExport.subscribe((result: ExportResult) => {
      this.exportBlasts(result.fileTypes, result.dxfVersion);
    });
  }

  exportBlasts(fileTypes: FileExportType[], dxfVersion?: number) {
    if (!this.tirId()) {
      console.error('Cannot export: tirId not set');
      this.fonctionsService.launch_toast(false, false, 'Erreur : Aucun blast sélectionné');
      return;
    }

    const fileTypeMap = {
      [FileExportType.STR]: 'STR',
      [FileExportType.DM4]: 'DM4',
      [FileExportType.DXF]: 'DXF'
    };

    const blastName = this.lastBlast?.nom || `blast_${this.tirId()}`;

    // If multiple file types selected, use the new endpoint that returns a ZIP
    if (fileTypes.length > 1) {
      this.httpService.exportBlastMultiple(this.tirId(), fileTypes, dxfVersion).subscribe({
        next: (response: Blob) => {
          const zipFileName = `${blastName}_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.zip`;

          // Create download link
          const url = window.URL.createObjectURL(response);
          const link = document.createElement('a');
          link.href = url;
          link.download = zipFileName;
          link.click();
          window.URL.revokeObjectURL(url);

          this.fonctionsService.launch_toast(true, false);
        },
        error: (error) => {
          console.error('Failed to export multiple files:', error);
          this.fonctionsService.launch_toast(false, false, 'Erreur lors de l\'export des fichiers');
        }
      });
    } else {
      // Single file export - use the original endpoint
      const fileType = fileTypes[0];
      const selectedFileTypeName = fileTypeMap[fileType];

      this.httpService.exportBlast(this.tirId(), fileType, dxfVersion).subscribe({
        next: (response: Blob) => {
          const extension = selectedFileTypeName.toLowerCase();
          const fileName = `${blastName}.${extension}`;

          // Create download link
          const url = window.URL.createObjectURL(response);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          link.click();
          window.URL.revokeObjectURL(url);

          this.fonctionsService.launch_toast(true, false);
        },
        error: (error) => {
          console.error(`Failed to export ${selectedFileTypeName}:`, error);
          this.fonctionsService.launch_toast(false, false, `Erreur lors de l'export ${selectedFileTypeName}`);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.clearGeology();
    this.resizeObserver?.disconnect();
    
    if (this.renderer) {
      this.renderer.dispose();
    }
    
    if (this.scene) {
      this.scene.clear();
    }
  }
}
